import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCountryQuery, useCreateCountryMutation, useUpdateCountryMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

const AdminCountryForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetCountryQuery(id!, { skip: !isEdit });
  const [createCountry, { isLoading: isCreating }] = useCreateCountryMutation();
  const [updateCountry, { isLoading: isUpdating }] = useUpdateCountryMutation();

  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    currency_code: '',
    currency_code_ar: '',
  });

  useEffect(() => {
    if (isEdit && data?.data) {
      const country = data.data;
      setFormData({
        name:             country.name             || '',
        name_ar:          country.name_ar          || '',
        currency_code:    country.currency_code    || '',
        currency_code_ar: country.currency_code_ar || '',
      });
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = formData.name.trim();
    const trimmedNameAr = formData.name_ar.trim();
    
    if (!trimmedName || trimmedName.length < 3) {
      showError(t('Name is required and must be at least 3 characters long.'));
      return;
    }
    
    if (!trimmedNameAr || trimmedNameAr.length < 3) {
      showError(t('Name Arabic is required and must be at least 3 characters long.'));
      return;
    }
    
    try {
      const submitData = {
        name:             trimmedName,
        name_ar:          trimmedNameAr,
        currency_code:    formData.currency_code.trim().toUpperCase() || null,
        currency_code_ar: formData.currency_code_ar.trim()            || null,
      };

      if (isEdit) {
        const result = await updateCountry({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || t('Country updated successfully!'));
      } else {
        const result = await createCountry(submitData).unwrap();
        showSuccess(result.message || t('Country created successfully!'));
      }
      
      navigate('/admin/countries');
    } catch (err: any) {
      if (err?.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat().join(', ');
        showError(errorMessages || err?.data?.message || t('Validation failed'));
      } else {
        showError(err?.data?.message || t(isEdit ? 'Failed to update country. Please try again.' : 'Failed to create country. Please try again.'));
      }
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? t('Edit Country') : t('Add New Country')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/countries'); }} className="text-muted text-hover-primary">{t('Countries')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Name')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Country Name')}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Name Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Country Name in Arabic')}
                        name="name_ar"
                        value={formData.name_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Currency Code')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('e.g. USD, AED, SAR')}
                        name="currency_code"
                        value={formData.currency_code}
                        onChange={handleInputChange}
                        maxLength={10}
                        style={{ textTransform: 'uppercase' }}
                      />
                      <div className="form-text">{t('ISO 4217 currency code (3 letters)')}</div>
                    </div>

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Currency Code Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('e.g. د.إ, ر.س, د.ك')}
                        name="currency_code_ar"
                        value={formData.currency_code_ar}
                        onChange={handleInputChange}
                        maxLength={20}
                      />
                      <div className="form-text">{t('Arabic currency symbol or code')}</div>
                    </div>

                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/countries')}>
                      {t('Discard')}
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">{t('Submit')}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCountryForm;

